import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  ArrowLeft, 
  User, 
  Calendar, 
  School, 
  MapPin, 
  Phone, 
  Mail,
  FileText,
  ClipboardList,
  Plus,
  Printer,
  Clock,
  Shield,
  FolderOpen,
  Edit
} from 'lucide-react';
import { AttachmentsList } from '../components/FileUpload';
import { PatientFlagsBanner } from '../components/ClinicalFlags';
import { RequestEditAccessButton, EditPermissionApprovalPanel } from '../components/EditPermissionManager';
import { PatientViewApprovalPanel, RequestPatientViewButton } from '../components/PatientViewApproval';
import { toast } from 'sonner';

export default function PatientDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [patient, setPatient] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPatientData();
  }, [id]);

  const fetchPatientData = async () => {
    try {
      const [patientRes, assessmentsRes, evaluationsRes, appointmentsRes, auditRes] = await Promise.all([
        api.get(`/patients/${id}`),
        api.get('/assessments', { params: { patient_id: id } }),
        api.get('/evaluations', { params: { patient_id: id } }),
        api.get('/appointments', { params: { patient_id: id } }),
        api.get(`/audit/patient/${id}`).catch(() => ({ data: [] }))
      ]);
      setPatient(patientRes.data);
      setAssessments(assessmentsRes.data);
      setEvaluations(evaluationsRes.data);
      setAppointments(appointmentsRes.data);
      setAuditLogs(auditRes.data);
    } catch (error) {
      console.error('Failed to fetch patient:', error);
      toast.error('Failed to load patient details');
    } finally {
      setLoading(false);
    }
  };

  const calculateAge = (dob) => {
    if (!dob) return '-';
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-[#1e3a5f] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Patient not found</p>
        <Button variant="link" onClick={() => navigate('/patients')}>
          Back to Patients
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Edit Permission Approval Panel - For approvers */}
      <EditPermissionApprovalPanel />
      
      {/* Patient View Approval Panel - For Lead Therapists */}
      <PatientViewApprovalPanel />
      
      {/* Clinical Safety Flags Banner */}
      <PatientFlagsBanner patientId={id} />
      
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/patients')}
          className="rounded-full"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-[#1e3a5f]">
            {patient.first_name} {patient.last_name}
          </h1>
          <p className="text-slate-500">
            <code className="bg-slate-100 px-2 py-0.5 rounded text-sm">{patient.reg_no}</code>
          </p>
        </div>
        <div className="flex gap-2 items-center flex-wrap justify-end">
          {/* Edit Permission Request Button */}
          <RequestEditAccessButton 
            patientId={id} 
            patientName={`${patient.first_name} ${patient.last_name}`}
          />
          <Button variant="outline" onClick={handlePrint} className="rounded-full no-print">
            <Printer className="w-4 h-4 mr-2" />
            Print
          </Button>
          <Button 
            className="bg-[#1e3a5f] hover:bg-[#1e3a5f]/90 rounded-full no-print"
            onClick={() => navigate(`/assessments/new/${patient.id}`)}
            data-testid="new-assessment-for-patient"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Assessment
          </Button>
        </div>
      </div>

      {/* Patient Header Card - Print Ready */}
      <Card className="card-icrcl print-header">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-[#1e3a5f]/10 flex items-center justify-center">
                <User className="w-8 h-8 text-[#1e3a5f]" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900">
                  {patient.first_name} {patient.last_name}
                </h2>
                <p className="text-slate-500">{patient.sex}</p>
                <Badge className={patient.status === 'active' ? 'badge-active' : 'badge-cancelled'}>
                  {patient.status}
                </Badge>
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-slate-600">
                <Calendar className="w-4 h-4" />
                <span>DOB: {new Date(patient.date_of_birth).toLocaleDateString('en-GB')}</span>
              </div>
              <div className="flex items-center gap-2 text-slate-600">
                <User className="w-4 h-4" />
                <span>Age: {calculateAge(patient.date_of_birth)} years</span>
              </div>
              {patient.nhs_number && (
                <div className="flex items-center gap-2 text-slate-600">
                  <FileText className="w-4 h-4" />
                  <span>NHS: {patient.nhs_number}</span>
                </div>
              )}
            </div>

            <div className="space-y-2">
              {patient.school && (
                <div className="flex items-center gap-2 text-slate-600">
                  <School className="w-4 h-4" />
                  <span>{patient.school}</span>
                </div>
              )}
              {patient.school_year && (
                <div className="flex items-center gap-2 text-slate-600">
                  <span className="ml-6">Year {patient.school_year}, Class {patient.school_class}</span>
                </div>
              )}
              {patient.council && (
                <div className="flex items-center gap-2 text-slate-600">
                  <MapPin className="w-4 h-4" />
                  <span>{patient.council}</span>
                </div>
              )}
            </div>

            <div className="space-y-2">
              {patient.gp_name && (
                <div className="text-slate-600">
                  <p className="font-medium">GP: {patient.gp_name}</p>
                  {patient.gp_phone && <p className="text-sm">{patient.gp_phone}</p>}
                </div>
              )}
              {patient.diagnoses?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {patient.diagnoses.map((d, i) => (
                    <Badge key={i} variant="outline" className="text-xs">
                      {d}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="assessments" className="no-print">
        <TabsList>
          <TabsTrigger value="assessments">
            <ClipboardList className="w-4 h-4 mr-2" />
            Assessments ({assessments.length})
          </TabsTrigger>
          <TabsTrigger value="evaluations">
            <FileText className="w-4 h-4 mr-2" />
            Reports ({evaluations.length})
          </TabsTrigger>
          <TabsTrigger value="appointments">
            <Calendar className="w-4 h-4 mr-2" />
            Appointments ({appointments.length})
          </TabsTrigger>
          <TabsTrigger value="chronology">
            <Shield className="w-4 h-4 mr-2" />
            Chronology ({auditLogs.length})
          </TabsTrigger>
          <TabsTrigger value="documents">
            <FolderOpen className="w-4 h-4 mr-2" />
            Documents
          </TabsTrigger>
        </TabsList>

        <TabsContent value="assessments" className="mt-6">
          <Card className="card-icrcl">
            <CardHeader>
              <CardTitle className="text-lg">Assessments</CardTitle>
              <CardDescription>Clinical assessments for this patient</CardDescription>
            </CardHeader>
            <CardContent>
              {assessments.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <ClipboardList className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No assessments yet</p>
                  <Button
                    variant="link"
                    className="text-[#2a9d8f]"
                    onClick={() => navigate(`/assessments/new/${patient.id}`)}
                  >
                    Create first assessment
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {assessments.map((assessment) => (
                    <div 
                      key={assessment.id}
                      className="flex items-center justify-between p-4 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer"
                      onClick={() => navigate(`/assessments/${assessment.id}`)}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-[#2a9d8f]/10 flex items-center justify-center">
                          <ClipboardList className="w-5 h-5 text-[#2a9d8f]" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">
                            {assessment.assessment_type.replace(/_/g, ' ').toUpperCase()}
                          </p>
                          <p className="text-sm text-slate-500">
                            {assessment.therapist_name} • {new Date(assessment.date).toLocaleDateString('en-GB')}
                          </p>
                        </div>
                      </div>
                      <Badge className={
                        assessment.status === 'completed' ? 'badge-completed' :
                        assessment.status === 'draft' ? 'badge-pending' :
                        'badge-active'
                      }>
                        {assessment.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="evaluations" className="mt-6">
          <Card className="card-icrcl">
            <CardHeader>
              <CardTitle className="text-lg">Evaluation Reports</CardTitle>
              <CardDescription>Generated evaluation reports</CardDescription>
            </CardHeader>
            <CardContent>
              {evaluations.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No evaluation reports yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {evaluations.map((eval_) => (
                    <div 
                      key={eval_.id}
                      className="flex items-center justify-between p-4 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-[#4ade80]/10 flex items-center justify-center">
                          <FileText className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">
                            {eval_.report_type.replace(/_/g, ' ').toUpperCase()} Report
                          </p>
                          <p className="text-sm text-slate-500">
                            {eval_.therapist_name} • {new Date(eval_.date).toLocaleDateString('en-GB')}
                          </p>
                        </div>
                      </div>
                      <Badge className={
                        eval_.status === 'final' ? 'badge-completed' :
                        eval_.status === 'draft' ? 'badge-pending' :
                        'badge-active'
                      }>
                        {eval_.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="appointments" className="mt-6">
          <Card className="card-icrcl">
            <CardHeader>
              <CardTitle className="text-lg">Appointments</CardTitle>
              <CardDescription>Scheduled and past appointments</CardDescription>
            </CardHeader>
            <CardContent>
              {appointments.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Calendar className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No appointments yet</p>
                  <Button
                    variant="link"
                    className="text-[#2a9d8f]"
                    onClick={() => navigate('/calendar')}
                  >
                    Schedule an appointment
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {appointments.map((apt) => (
                    <div 
                      key={apt.id}
                      className="flex items-center justify-between p-4 rounded-lg bg-slate-50"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-[#f472b6]/10 flex items-center justify-center">
                          <Clock className="w-5 h-5 text-pink-500" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{apt.appointment_type}</p>
                          <p className="text-sm text-slate-500">
                            {apt.therapist_name} • {new Date(apt.date).toLocaleDateString('en-GB')} at {apt.start_time}
                          </p>
                        </div>
                      </div>
                      <Badge className={
                        apt.status === 'completed' ? 'badge-completed' :
                        apt.status === 'scheduled' ? 'badge-pending' :
                        apt.status === 'cancelled' ? 'badge-cancelled' :
                        'badge-active'
                      }>
                        {apt.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="chronology" className="mt-6">
          <Card className="card-icrcl">
            <CardHeader>
              <CardTitle className="text-lg">Patient Chronology</CardTitle>
              <CardDescription>Complete audit trail for this patient</CardDescription>
            </CardHeader>
            <CardContent>
              {auditLogs.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Shield className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No audit logs available</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {auditLogs.map((log) => (
                    <div 
                      key={log.id}
                      className="flex items-start gap-4 p-4 rounded-lg bg-slate-50"
                    >
                      <div className="w-2 h-2 rounded-full bg-[#1e3a5f] mt-2"></div>
                      <div className="flex-1">
                        <p className="font-medium text-slate-900">
                          {log.action} - {log.entity_type}
                        </p>
                        <p className="text-sm text-slate-500">
                          By {log.user_name} • {new Date(log.timestamp).toLocaleString('en-GB')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="documents" className="mt-6">
          <AttachmentsList
            entityType="child"
            entityId={id}
            title="Documents & Media"
            showUploadButton={true}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
